"use client";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useUpload } from "@/providers/UploadProvider";
import { AlertTriangle } from "lucide-react";

/**
 * Modal that displays upload validation errors to the user.
 * This is shown when the user tries to upload too many files,
 * files that are too large, or unsupported file types.
 */
export function UploadValidationModal() {
  const { validationError, clearValidationError } = useUpload();

  // Parse the error message to provide more helpful context
  const getErrorDetails = (error: string) => {
    if (error.includes("Cannot upload more than")) {
      // Extract the number from the error message
      const match = error.match(/(\d+)/);
      const maxFiles = match ? match[1] : "5000";
      
      return {
        title: "Too Many Files Selected",
        description: error,
        suggestion: `Please select ${maxFiles} or fewer files and try again. You can upload additional files in separate batches.`,
        icon: "files",
      };
    }
    
    if (error.includes("too large")) {
      return {
        title: "File Too Large",
        description: error,
        suggestion: "Please compress the file or select a smaller one.",
        icon: "size",
      };
    }
    
    if (error.includes("Only JPEG and PNG")) {
      return {
        title: "Unsupported File Type",
        description: error,
        suggestion: "Please convert your files to JPEG or PNG format before uploading.",
        icon: "type",
      };
    }
    
    // Default case
    return {
      title: "Upload Error",
      description: error,
      suggestion: "Please check your files and try again.",
      icon: "default",
    };
  };

  if (!validationError) {
    return null;
  }

  const errorDetails = getErrorDetails(validationError);

  return (
    <AlertDialog open={!!validationError} onOpenChange={(open) => !open && clearValidationError()}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100">
            <AlertTriangle className="h-7 w-7 text-amber-600" />
          </div>
          <AlertDialogTitle className="text-center text-xl">
            {errorDetails.title}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-center space-y-3">
            <p className="text-[#3B3C4A] font-medium">
              {errorDetails.description}
            </p>
            <p className="text-[#6F7083] text-sm">
              {errorDetails.suggestion}
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="sm:justify-center">
          <AlertDialogAction
            onClick={clearValidationError}
            className="bg-[#6E3CAF] hover:bg-[#5b2f93] text-white px-8"
          >
            Got it
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

